/* eslint-disable */
import React from 'react';
import {
  Box,
  Card,
  Typography,
  Avatar,
  Chip,
  Stack,
  Divider,
  Tooltip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTheme } from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import SchoolIcon from '@mui/icons-material/School';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';

const PairCard = ({
  pairData,
  isEndingTraining,
  isEndingPairing,
  setTrainingEndingPayload,
  setPairingEndingPayload,
  index,
}) => {
  const theme = useTheme();

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Card
      sx={{
        position: 'relative',
        borderRadius: '16px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(86, 156, 211, 0.15)',
          borderColor: 'primary.light',
        },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #569cd3 0%, #7DB8E5 100%)',
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SupervisorAccountIcon sx={{ color: 'white', fontSize: 20 }} />
          <Typography
            variant="subtitle1"
            sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: '15px',
            }}
          >
            Pair #{index + 1}
          </Typography>
        </Box>

        {pairData?.lastSession && (
          <Chip
            label="Training Ended"
            size="small"
            sx={{
              background: 'rgba(255, 255, 255, 0.25)',
              color: 'white',
              fontWeight: 600,
              fontSize: '11px',
              height: '24px',
            }}
          />
        )}
      </Box>

      <Box sx={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Teacher Profile */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            background: 'linear-gradient(135deg, rgba(168, 70, 155, 0.04) 0%, rgba(168, 70, 155, 0.08) 100%)',
            borderRadius: '12px',
            mb: 1.5,
            transition: 'all 0.2s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(168, 70, 155, 0.08) 0%, rgba(168, 70, 155, 0.12) 100%)',
            },
          }}
        >
          <Avatar
            src={pairData?.['Teacher Image'] || undefined}
            sx={{
              width: 56,
              height: 56,
              background: pairData?.['Teacher Image'] ? 'transparent' : 'linear-gradient(135deg, #A8469B 0%, #C567B8 100%)',
              fontSize: '20px',
              fontWeight: 700,
              boxShadow: '0 4px 12px rgba(168, 70, 155, 0.25)',
            }}
          >
            {!pairData?.['Teacher Image'] && getInitials(pairData?.['Teacher Name'])}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <SchoolIcon sx={{ fontSize: 16, color: '#A8469B' }} />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: '#A8469B',
                  fontSize: '14px',
                }}
              >
                Teacher
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: '15px',
                mb: 0.5,
                lineHeight: 1.2,
              }}
            >
              {pairData?.['Teacher Name']}
            </Typography>
            <Tooltip title={pairData?.['Teacher Email']} arrow placement="top">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                }}
              >
                <EmailIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '12px',
                    color: theme.palette.text.secondary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {pairData?.['Teacher Email']}
                </Typography>
              </Box>
            </Tooltip>
          </Box>
        </Box>

        {/* Connection Indicator */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            my: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 0.75,
              background: 'linear-gradient(90deg, #A8469B 0%, #569cd3 100%)',
              borderRadius: '20px',
              boxShadow: '0 2px 8px rgba(86, 156, 211, 0.25)',
            }}
          >
            <SwapVertIcon
              sx={{
                color: 'white',
                fontSize: 20,
                animation: 'slideHorizontal 2s ease-in-out infinite',
                '@keyframes slideHorizontal': {
                  '0%, 100%': { transform: 'translateY(0)' },
                  '50%': { transform: 'translateY(4px)' },
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'white',
                fontWeight: 600,
                fontSize: '11px',
                letterSpacing: '0.5px',
              }}
            >
              PAIRED
            </Typography>
          </Box>
        </Box>

        {/* Edifier Profile */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            background: 'linear-gradient(135deg, rgba(86, 156, 211, 0.04) 0%, rgba(86, 156, 211, 0.08) 100%)',
            borderRadius: '12px',
            mb: 2,
            transition: 'all 0.2s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(86, 156, 211, 0.08) 0%, rgba(86, 156, 211, 0.12) 100%)',
            },
          }}
        >
          <Avatar
            src={pairData?.['Edifier Image'] || undefined}
            sx={{
              width: 56,
              height: 56,
              background: pairData?.['Edifier Image'] ? 'transparent' : 'linear-gradient(135deg, #569cd3 0%, #7DB8E5 100%)',
              fontSize: '20px',
              fontWeight: 700,
              boxShadow: '0 4px 12px rgba(86, 156, 211, 0.25)',
            }}
          >
            {!pairData?.['Edifier Image'] && getInitials(pairData?.['Edifier Name'])}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <SupervisorAccountIcon sx={{ fontSize: 16, color: '#569cd3' }} />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: '#569cd3',
                  fontSize: '14px',
                }}
              >
                Edifier
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: '15px',
                mb: 0.5,
                lineHeight: 1.2,
              }}
            >
              {pairData?.['Edifier Name']}
            </Typography>
            <Tooltip title={pairData?.['Edifier Email']} arrow placement="top">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                }}
              >
                <EmailIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '12px',
                    color: theme.palette.text.secondary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {pairData?.['Edifier Email']}
                </Typography>
              </Box>
            </Tooltip>
          </Box>
        </Box>

        {/* Divider */}
        <Divider sx={{ mb: 2 }} />

        {/* Paired Since Info */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            px: 1,
          }}
        >
          <CalendarMonthIcon
            sx={{
              color: theme.palette.text.secondary,
              fontSize: 18,
            }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '11px',
                fontWeight: 500,
                display: 'block',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Paired Since
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 600,
                fontSize: '13px',
              }}
            >
              {pairData?.['Paired Since']}
            </Typography>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Stack spacing={1.5} sx={{ mt: 'auto' }}>
          <LoadingButton
            variant="contained"
            loading={isEndingTraining}
            disabled={pairData?.lastSession || isEndingPairing}
            onClick={() => setTrainingEndingPayload({ id: pairData?._id })}
            fullWidth
            sx={{
              height: '40px',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              background: pairData?.lastSession
                ? '#E0E0E0'
                : '#54D62C',
              color: pairData?.lastSession ? '#757575' : 'white',
              boxShadow: pairData?.lastSession ? 'none' : '0 2px 8px rgba(84, 214, 44, 0.3)',
              '&:hover': {
                background: pairData?.lastSession
                  ? '#E0E0E0'
                  : '#229A16',
                boxShadow: pairData?.lastSession
                  ? 'none'
                  : '0 4px 12px rgba(84, 214, 44, 0.4)',
              },
            }}
          >
            {pairData?.lastSession ? 'Training Ended' : 'End Training'}
          </LoadingButton>

          <LoadingButton
            variant="outlined"
            loading={isEndingPairing}
            onClick={() => setPairingEndingPayload({ ...pairData?.usersInPair })}
            fullWidth
            sx={{
              height: '40px',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              borderWidth: '1.5px',
              borderColor: '#FF4842',
              color: '#FF4842',
              '&:hover': {
                borderWidth: '1.5px',
                borderColor: '#FF4842',
                background: 'rgba(255, 72, 66, 0.08)',
              },
            }}
          >
            Unpair
          </LoadingButton>
        </Stack>
      </Box>
    </Card>
  );
};

export default PairCard;
